/**
 * Mapping for advance types and their associated service providers and IDs
 */

import { IAdvanceRequestType } from "./AdvanceRequestTypes";
import { IEmployeeType } from "./EmployeeTypes";

export enum AdvanceType {
  EWA = 'EWA',
  VAS = 'VAS',
}

export enum ServiceProvider {
  STITCH = 'STITCH',
  FLASH = 'FLASH',
}

/**
 * Service provider names as they appear in metadata
 */
export const SERVICE_PROVIDER_NAMES = {
  SHOPRITE: 'shoprite',
  FLASH: 'flash',
  STITCH: 'stitch',
} as const;

/**
 * Transaction types for VAS products
 */
export enum TransactionType {
  AIRTIME = 'airtime',
  DATA = 'data',
  ELECTRICITY = 'electricity',
  SHOPRITE_MONEY_MARKET_VOUCHER = 'shoprite_moneymarket_voucher'
}

/**
 * Payment types for EWA products
 */
export enum PaymentType {
  INSTANT = 'instant',
  STANDARD = 'standard',
}

/**
 * Display names for payment types
 */
export const PAYMENT_TYPE_DISPLAY = {
  [PaymentType.INSTANT]: 'Instant',
  [PaymentType.STANDARD]: 'Standard',
};

/**
 * Display names for transaction types
 */
export const TRANSACTION_TYPE_DISPLAY = {
  [TransactionType.AIRTIME]: 'Airtime',
  [TransactionType.DATA]: 'Data',
  [TransactionType.ELECTRICITY]: 'Electricity',
  [TransactionType.SHOPRITE_MONEY_MARKET_VOUCHER]: 'Shoprite MMA',
};

interface AdvanceTypeConfig {
  title: string;
  idLabel: string;
  serviceProvider: ServiceProvider;
  possibleTransactionTypes?: TransactionType[];
}

/**
 * Mapping configuration for each advance type
 */
export const ADVANCE_TYPE_CONFIG: Record<AdvanceType, AdvanceTypeConfig> = {
  [AdvanceType.EWA]: {
    title: 'Earned Wage Access',
    idLabel: 'stitch',
    serviceProvider: ServiceProvider.STITCH,
  },
  [AdvanceType.VAS]: {
    title: 'Value Added Services',
    idLabel: 'flash',
    serviceProvider: ServiceProvider.FLASH,
    possibleTransactionTypes: [
      TransactionType.AIRTIME,
      TransactionType.DATA,
      TransactionType.ELECTRICITY,
      TransactionType.SHOPRITE_MONEY_MARKET_VOUCHER,
    ],
  },
};

/**
 * Get the ID label for a specific advance type
 * @param advanceType The type of advance
 * @param serviceProvider Optional service provider from metadata
 * @returns The label for the ID field
 */
export const getAdvanceIdLabel = (advanceType: string, serviceProvider?: string, paymentProvider?: string): string => {
  if (advanceType === AdvanceType.VAS) {
    return serviceProvider || paymentProvider || ADVANCE_TYPE_CONFIG[AdvanceType.VAS].idLabel;
  }

  if (advanceType === AdvanceType.EWA) {
    return paymentProvider || ADVANCE_TYPE_CONFIG[AdvanceType.EWA].idLabel;
  }

  return advanceType in ADVANCE_TYPE_CONFIG
    ? ADVANCE_TYPE_CONFIG[advanceType as AdvanceType].idLabel
    : '';
};

/**
 * Get the type display name for the Type column
 * @param advanceType The type of advance
 * @returns A formatted type display name
 */
export const getTypeDisplayName = (
  advanceType: string
): string => {
  if (advanceType === AdvanceType.VAS) {
    return AdvanceType.VAS;
  }
  
  if (advanceType === AdvanceType.EWA) {
    return AdvanceType.EWA;
  }
  
  return advanceType; // default to advance_type from API call - currently "EWA" or "VAS"
};

/**
 * Type guard to check if a string is a valid TransactionType
 */
const isValidTransactionType = (type: string): type is TransactionType => Object.values(TransactionType).includes(type as TransactionType);

/**
 * Get VAS product display name based on transaction type and metadata
 * @param metadata Metadata containing transaction details
 * @returns Formatted VAS product display name
 */
const getVasProductDisplayName = (metadata?: { 
  data?: { 
    transaction_type?: string;
    network_name?: string;
    data_package_selected?: string;
  } 
}): string => {
  if (!metadata?.data) {
    return AdvanceType.VAS;
  }

  const { transaction_type, network_name } = metadata.data;

  // Handle data bundles
  if (transaction_type === TransactionType.DATA && network_name) {
    return network_name;
  }
  
  // Handle other transaction types
  if (transaction_type && isValidTransactionType(transaction_type)) {
    return TRANSACTION_TYPE_DISPLAY[transaction_type];
  }
  
  return transaction_type || AdvanceType.VAS;
};

/**
 * Get EWA product display name based on payment type
 * @param isInstantPayment Whether the payment is instant
 * @returns Formatted EWA product display name
 */
const getEwaProductDisplayName = (isInstantPayment?: boolean): string => {
  const paymentType = isInstantPayment ? PaymentType.INSTANT : PaymentType.STANDARD;
  return PAYMENT_TYPE_DISPLAY[paymentType];
};

/**
 * Get the product display name based on advance type and metadata
 * @param advanceType The type of advance
 * @param metadata Optional metadata containing transaction details
 * @param isInstantPayment Whether the payment is instant (for EWA)
 * @returns A formatted product display name
 */
export const getProductDisplayName = (
  advanceType: string,
  metadata?: { 
    type?: string; 
    data?: { 
      transaction_type?: string;
      network_name?: string;
      data_package_selected?: string;
    } 
  },
  isInstantPayment?: boolean
): string => {
  // Handle different advance types
  switch (advanceType) {
    case AdvanceType.VAS:
      return getVasProductDisplayName(metadata);
    case AdvanceType.EWA:
      return getEwaProductDisplayName(isInstantPayment);
    default:
      return advanceType; // default to advance_type from API call
  }
};

/**
 * Get the correct label and value for the "account" column (Meter Number or Phone Number)
 * @param advance The advance object (IAdvanceRequestType)
 * @param employee The employee object (IEmployeeType)
 * @returns { label: string; value: string }
 */
export function getAdvanceAccountLabelAndValue(
  advance: IAdvanceRequestType,
  employee: IEmployeeType
): { label: string; value: string } {
  const transactionType = advance.metadata?.data?.transaction_type;
  const serviceProvider = advance.metadata?.service_provider;
  
  if (advance.advance_type === AdvanceType.VAS) {
    if (transactionType === TransactionType.ELECTRICITY) {
      return {
        label: 'Meter Number',
        value: advance.metadata?.data?.meter_number || '',
      };
    }
    
    if (transactionType === TransactionType.SHOPRITE_MONEY_MARKET_VOUCHER || serviceProvider === SERVICE_PROVIDER_NAMES.SHOPRITE) {
      return {
        label: 'Recipient Cellphone',
        value: advance.metadata?.data?.voucher_mobile_number || advance.metadata?.data?.mobile_number || '',
      };
    }
  }
  
  // For all others, use Phone Number
  return {
    label: 'Phone Number',
    value: employee.mobile_number,
  };
}
